import { type ObrasFiltros, cliente } from '@/lib/cliente';
import type { HitoDesembolsoCodigo, IniciarObra, IniciarObraLote } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Catálogo de sprints (nombre real + nº de críticas). Es estático: se cachea
 * largo para no repetir el fetch en cada render del Kanban.
 */
export function useSprintsCatalogo() {
  return useQuery({
    queryKey: ['sprints', 'catalogo'],
    queryFn: () => cliente.sprints.catalogo(),
    staleTime: 1000 * 60 * 30,
  });
}

/** Marca/desmarca un sprint como "de espera". Refresca el catálogo al guardar. */
export function useMarcarSprintEspera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ numero, es_espera }: { numero: number; es_espera: boolean }) =>
      cliente.sprints.marcarEspera(numero, es_espera),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sprints', 'catalogo'] }),
  });
}

/** Asigna (o quita) el hito de desembolso de un sprint. Refresca el catálogo al guardar. */
export function useMarcarSprintHito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      numero,
      hito_desembolso,
    }: { numero: number; hito_desembolso: HitoDesembolsoCodigo | null }) =>
      cliente.sprints.marcarHito(numero, hito_desembolso),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sprints', 'catalogo'] }),
  });
}

/**
 * Hook que lista obras con filtros opcionales.
 * El queryKey incluye los filtros para que cache TanStack los maneje bien.
 */
export function useObras(filtros: ObrasFiltros = {}) {
  return useQuery({
    queryKey: ['obras', filtros],
    queryFn: () => cliente.obras.listar(filtros),
    // Toca vw_obras (lenta) y cambia solo por ETL / al habilitar (que invalida
    // esta query). Cacheamos largo para no re-pedirla en cada navegación.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/**
 * Estado operativo liviano de todas las obras (solo obc.obra_estado, sin
 * vw_obras). Para el Kanban (columnas Por Iniciar / Terminadas) — rápido.
 */
export function useObrasEstados() {
  return useQuery({
    queryKey: ['obra-estados'],
    queryFn: () => cliente.obras.estados(),
    staleTime: 30_000,
  });
}

/** Hook que obtiene una sola obra por código (ej. 'VN-G.01'). */
export function useObra(codigo: string | undefined) {
  return useQuery({
    queryKey: ['obras', codigo],
    queryFn: () => {
      if (!codigo) throw new Error('Código de obra requerido');
      return cliente.obras.obtener(codigo);
    },
    enabled: !!codigo,
    staleTime: 5 * 60_000,
  });
}

/**
 * Habilita / configura una obra desde admin: fija tipo_casa + sprint inicial
 * (estado en_ejecucion). Refresca el listado de obras y el avance de la obra.
 */
export function useHabilitarObra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codigo, body }: { codigo: string; body: IniciarObra }) =>
      cliente.obras.iniciar(codigo, body),
    onSuccess: (_res, { codigo }) => {
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['obra-estados'] });
      qc.invalidateQueries({ queryKey: ['avance', codigo] });
      // El Kanban se dibuja desde el preview ['cierre']: refrescarlo para que
      // la obra aparezca/desaparezca de su columna al cambiar de estado.
      qc.invalidateQueries({ queryKey: ['cierre'] });
    },
  });
}

/** Habilita varias obras de una (tipo_casa + sprint, o 'auto' por obra). */
export function useHabilitarObrasLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IniciarObraLote) => cliente.obras.iniciarLote(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['obra-estados'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
    },
  });
}

/** Avanza una obra al siguiente sprint según tipo_casa_sprints. */
export function useAvanzarSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (codigo: string) => cliente.obras.avanzarSprint(codigo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
      qc.invalidateQueries({ queryKey: ['avance'] });
      qc.invalidateQueries({ queryKey: ['matriz'] });
    },
  });
}

/** Retrocede una obra al sprint anterior (solo si es de espera). */
export function useRetrocederSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (codigo: string) => cliente.obras.retrocederSprint(codigo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['obra-estados'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
      qc.invalidateQueries({ queryKey: ['avance'] });
      qc.invalidateQueries({ queryKey: ['matriz'] });
    },
  });
}

/** Cambia el estado de una obra (en_espera = congelar, en_ejecucion = reactivar, ...). */
export function useSetEstadoObra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      codigo,
      estado,
      motivo,
    }: { codigo: string; estado: string; motivo?: string | null }) =>
      cliente.obras.setEstado(codigo, estado, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obras'] });
      qc.invalidateQueries({ queryKey: ['obra-estados'] });
      qc.invalidateQueries({ queryKey: ['cierre'] });
    },
  });
}
